import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import '../styles/RunPayroll.css';
import '../styles/StatCard.css';
import { FiPlay, FiChevronRight, FiDollarSign, FiPercent, FiShield, FiCreditCard, FiUsers, FiCalendar, FiSearch, FiDownload, FiClock } from 'react-icons/fi';
import StatCard from './StatCard';
import Cookies from 'js-cookie';
import CommonLoader from '../../../components/common/CommonLoader';
import { GRAPHQL_HTTP_URL, API_ORIGIN } from '../../../config/api';

const GQL = GRAPHQL_HTTP_URL;
const API_BASE = API_ORIGIN;

async function gqlRequest({ token, query, variables, signal }) {
    const res = await fetch(GQL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
        signal,
    });
    const json = await res.json();
    if (!res.ok || json?.errors?.length) {
        throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
    }
    return json.data;
}

const LAST_PAY_PERIOD_QUERY = `
  query LastPayPeriod {
    lastPayPeriod {
      periodEnd
    }
  }
`;

const TIME_TRACKING_QUERY = `
  query PayrollTimeTracking($filter: TimeTrackingFilterInput!) {
    payrollTimeTracking(filter: $filter) {
      entries {
        workDate
        regularHours
        overtimeHours
        leaveType
      }
    }
  }
`;

const RUN_PAYROLL_QUERY = `
  query RunPayroll($filter: RunPayrollFilterInput!) {
    runPayroll(filter: $filter) {
      totalGrossPayable
      totalPaygDeductions
      totalNetPayable
      totalSuperannuationContribution
      employeeCount
      abaPayRunId
      abaStatus
      alreadyLockedCount
      employees {
        employeeId
        employeeName
        employmentType
        workedHours
        payRate
        overtimeHours
        saturdayHours
        sundayHours
        paidLeaveHours
        unpaidLeaveHours
        regularPay
        paidLeavePay
        ordinaryTimeEarnings
        overtimePay
        saturdayPay
        sundayPay
        overtimeMultiplier
        overtimeDoubleMultiplier
        saturdayMultiplier
        sundayMultiplier
        otTier1Hours
        otTier2Hours
        otTier1Pay
        otTier2Pay
        casualLoadingRate
        callBackHoursApplied
        annualLeaveAccrued
        sickLeaveAccrued
        workedRatio
        earnings
        deductions
        incomeTax
        medicareLevy
        stslRepayment
        superannuationRate
        superannuationAmount
        netPay
        payslipId
        payslipStatus
        pdfUrl
        bsb
        accountNumber
        paymentMethod
        isTaxCalculated
      }
    }
  }
`;

const GENERATE_PAYSLIP_MUTATION = `
  mutation GeneratePayslip($empId: String!, $start: String!, $end: String!) {
    generatePayslip(employeeId: $empId, startDate: $start, endDate: $end) {
      id
      pdfUrl
    }
  }
`;

const GENERATE_PAYSLIP_ID_ONLY_MUTATION = `
  mutation GeneratePayslip($empId: String!, $start: String!, $end: String!) {
    generatePayslip(employeeId: $empId, startDate: $start, endDate: $end) {
      id
    }
  }
`;

const SAVE_PAYMENT_MODE_MUTATION = `
  mutation SavePaymentMode($input: SavePaymentModeInput!) {
    savePaymentMode(input: $input)
  }
`;

const EXECUTE_PAYROLL_RUN_MUTATION = `
  mutation ExecutePayrollRun($input: ExecutePayrollRunInput!) {
    executePayrollRun(input: $input) {
      successCount
      failedCount
      abaGenerated
      abaPayRunId
      abaError
      abaExcludedRecipients { recipientName bsb accountNumber reason }
      stpSubmitted
      stpSubmissionId
      stpError
      employees {
        employeeId
        payslipId
        success
        error
      }
    }
  }
`;

const VOID_ABA_MUTATION = `
  mutation VoidAba($id: String!, $reason: String!) {
    voidAbaPayRun(id: $id, reason: $reason) {
      id
      status
      voidReason
    }
  }
`;

// Use local year/month/day — avoids UTC toISOString() giving wrong date in AEST/non-UTC timezones
const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmt = (n) => `$${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateShort = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

const fmtMonthYear = (start, end) => {
    if (!start || !end) return '';
    return `${fmtDateShort(start)} – ${fmtDateShort(end)}`;
};

const getDayType = (dateStr) => {
    if (!dateStr) return '—';
    // UTC-safe to avoid timezone off-by-one on date-only strings
    const [y, m, d] = dateStr.split('-').map(Number);
    const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    if (day === 0) return 'Sunday';
    if (day === 6) return 'Saturday';
    return 'Weekday';
};

// Payroll rounding rule — matches backend calculateHoursBreakdown logic:
//   0 – 7 min  → 0 min  (not payable)
//   8 – 49 min → 50 min (rounded up to minimum payable block)
//   50+ min    → unchanged
const roundWorkedMinutes = (minutes) => {
    if (minutes <= 7) return 0;
    if (minutes < 50) return 50;
    return minutes;
};

// Convert raw hours to payroll-rounded hours (used in breakdown table only)
const toRoundedHours = (rawHours) => {
    const rawMinutes = Math.round(Number(rawHours || 0) * 60);
    return roundWorkedMinutes(rawMinutes) / 60;
};

// Day type badge component for the breakdown table
const DayBadge = ({ dateStr }) => {
    const type = getDayType(dateStr);
    const cls = type === 'Saturday' ? 'saturday' : type === 'Sunday' ? 'sunday' : 'weekday';
    return <span className={`rp-day-badge ${cls}`}>{type}</span>;
};

// Returns all weekdays (Mon–Fri) between start and end date (inclusive) as YYYY-MM-DD strings
const getWeekdaysInRange = (start, end) => {
    const days = [];
    const current = new Date(start + 'T00:00:00');
    const last    = new Date(end   + 'T00:00:00');
    while (current <= last) {
        const [y, m, d] = [current.getFullYear(), current.getMonth(), current.getDate()];
        const dow = new Date(Date.UTC(y, m, d)).getUTCDay();
        if (dow !== 0 && dow !== 6) days.push(`${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
        current.setDate(current.getDate() + 1);
    }
    return days;
};

// Use backend-calculated values (which apply PayrollSettings multipliers per employment type)
// These helpers use backend-calculated values (regularPay / overtimePay) when present.
// The arithmetic fallback is never expected to trigger since the runPayroll query always
// returns calculated values; it exists only as a last-resort safety net.
const calcRegularPay  = (emp) => Number(emp.regularPay  ?? (Number(emp.hours  || 0) * Number(emp.rate || 0)));
const calcOvertimePay = (emp) => Number(emp.overtimePay ?? (Number(emp.otHours || 0) * Number(emp.rate || 0) * Number(emp.overtimeMultiplier ?? 1.5)));

export default function RunPayroll({ onPayrollComplete, onViewTimesheet, onGoToAbaRuns }) {
    const [dateRange, setDateRange] = useState('thisMonth');
    const [customStart, setCustomStart] = useState(toLocalDateStr(new Date()));
    const [customEnd, setCustomEnd] = useState(toLocalDateStr(new Date()));
    const [filter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [periodType, setPeriodType] = useState('Month');
    const [modal, setModal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState(null);
    const [abaWarning, setAbaWarning] = useState(null);
    const [stpResult, setStpResult]   = useState(null);   // { submitted, submissionId, error }
    const [generatingForId, setGeneratingForId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [periodAbaStatus, setPeriodAbaStatus] = useState(null);   // { abaPayRunId, abaStatus }
    const [alreadyLockedCount, setAlreadyLockedCount] = useState(0);
    const [nextPeriodSuggestion, setNextPeriodSuggestion] = useState(null); // { suggestedStart, suggestedEnd }

    // Void ABA (from Run Payroll screen)
    const [voidAbaOpen, setVoidAbaOpen] = useState(false);
    const [voidAbaReason, setVoidAbaReason] = useState('');
    const [voidAbaBusy, setVoidAbaBusy] = useState(false);
    const [voidAbaError, setVoidAbaError] = useState(null);

    // Payment modes and tax choices are stored in the DB (payment_mode_preference table).
    // Loaded as part of the runPayroll query response; saved immediately on change via mutation.
    const [paymentModes, setPaymentModes] = useState({});
    const [taxChoices, setTaxChoices] = useState({});
    const [cashTaxPopup, setCashTaxPopup] = useState(null); // { empId, empName }
    const [noEmployeePopup, setNoEmployeePopup] = useState(false);
    const [employeesData, setEmployeesData] = useState([]);
    const [detailEntries, setDetailEntries] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const token = Cookies.get('accessToken');

    const { startDate, endDate } = useMemo(() => {
        const today = new Date();
        const todayStr = toLocalDateStr(today);

        // Custom range overrides period type
        if (dateRange === 'custom') {
            return { startDate: customStart, endDate: customEnd };
        }

        // Period type drives the date range
        if (periodType === 'Week') {
            // Monday → Sunday of current week
            const dow = today.getDay(); // 0=Sun … 6=Sat
            const diffToMon = (dow === 0 ? -6 : 1 - dow);
            const mon = new Date(today); mon.setDate(today.getDate() + diffToMon);
            const sun = new Date(mon);   sun.setDate(mon.getDate() + 6);
            return { startDate: toLocalDateStr(mon), endDate: toLocalDateStr(sun) };
        }

        if (periodType === 'Fortnight') {
            // 14-day period ending today
            const s = new Date(today); s.setDate(today.getDate() - 13);
            return { startDate: toLocalDateStr(s), endDate: todayStr };
        }

        if (periodType === 'LastMonth') {
            // 1st of previous month → last day of previous month
            const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const e = new Date(today.getFullYear(), today.getMonth(), 0);
            return { startDate: toLocalDateStr(s), endDate: toLocalDateStr(e) };
        }

        // Month (default) — 1st of current month → last day of current month
        const s = new Date(today.getFullYear(), today.getMonth(), 1);
        const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { startDate: toLocalDateStr(s), endDate: toLocalDateStr(e) };
    }, [periodType, dateRange, customStart, customEnd]);

    // Extracted as useCallback so it can be called directly after
    // generating payslips — ensures modal always shows fresh backend data.
    const loadPayrollData = React.useCallback(async () => {
        if (!token) {
            setLoadError('Authentication required. Please login again.');
            setEmployeesData([]);
            return [];
        }
        if (!startDate || !endDate) return [];
        setLoading(true);
        setLoadError(null);
        try {
            const data = await gqlRequest({
                token,
                query: RUN_PAYROLL_QUERY,
                variables: { filter: { startDate, endDate } },
            });
            const rp = data?.runPayroll;
            if (rp) {
                setPeriodAbaStatus(
                    rp.abaPayRunId
                        ? { abaPayRunId: rp.abaPayRunId, abaStatus: rp.abaStatus }
                        : null
                );
                setAlreadyLockedCount(rp.alreadyLockedCount ?? 0);
                const mapped = (rp.employees ?? []).map((r) => ({
                    id: r.employeeId,
                    name: r.employeeName,
                    type: r.employmentType || '-',
                    hours: r.workedHours ?? 0,
                    rate: r.payRate ?? 0,
                    otHours: r.overtimeHours ?? 0,
                    saturdayHours: r.saturdayHours ?? 0,
                    sundayHours: r.sundayHours ?? 0,
                    paidLeave: r.paidLeaveHours ?? 0,
                    unpaidLeave: r.unpaidLeaveHours ?? 0,
                    regularPay: r.regularPay ?? null,
                    paidLeavePay: r.paidLeavePay ?? 0,
                    ordinaryTimeEarnings: r.ordinaryTimeEarnings ?? null,
                    overtimePay: r.overtimePay ?? null,
                    saturdayPay: r.saturdayPay ?? null,
                    sundayPay: r.sundayPay ?? null,
                    overtimeMultiplier: r.overtimeMultiplier ?? null,
                    overtimeDoubleMultiplier: r.overtimeDoubleMultiplier ?? null,
                    saturdayMultiplier: r.saturdayMultiplier ?? null,
                    sundayMultiplier: r.sundayMultiplier ?? null,
                    otTier1Hours: r.otTier1Hours ?? 0,
                    otTier2Hours: r.otTier2Hours ?? 0,
                    otTier1Pay: r.otTier1Pay ?? 0,
                    otTier2Pay: r.otTier2Pay ?? 0,
                    casualLoadingRate: r.casualLoadingRate ?? 0,
                    callBackHoursApplied: r.callBackHoursApplied ?? 0,
                    annualLeaveAccrued: r.annualLeaveAccrued ?? 0,
                    sickLeaveAccrued: r.sickLeaveAccrued ?? 0,
                    workedRatio: r.workedRatio ?? 0,
                    earnings: r.earnings ?? 0,
                    deductions: r.deductions ?? 0,
                    incomeTax: r.incomeTax ?? 0,
                    medicareLevy: r.medicareLevy ?? 0,
                    stslRepayment: r.stslRepayment ?? 0,
                    superannuationRate: r.superannuationRate ?? 12,
                    superAmt: r.superannuationAmount ?? 0,
                    netPay: r.netPay ?? 0,
                    payslipStatus: r.payslipStatus ?? null,
                    pdfUrl: r.pdfUrl ?? null,
                    payslipId: r.payslipId ?? null,
                    bsb: r.bsb ?? null,
                    accountNumber: r.accountNumber ?? null,
                    paymentMethod: r.paymentMethod ?? 'BANK',
                    isTaxCalculated: r.isTaxCalculated ?? true,
                }));
                setEmployeesData(mapped);
                // Seed payment mode state from DB values returned by the query
                const modes = {};
                const taxes = {};
                mapped.forEach(e => {
                    modes[e.id] = e.paymentMethod;
                    taxes[e.id] = e.isTaxCalculated;
                });
                setPaymentModes(modes);
                setTaxChoices(taxes);
                return mapped;
            } else {
                setEmployeesData([]);
                return [];
            }
        } catch (err) {
            console.error(err);
            setEmployeesData([]);
            setLoadError(err.message || 'Failed to load payroll data.');
            return [];
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, token]);

    React.useEffect(() => {
        loadPayrollData();
    }, [loadPayrollData]);

    // On mount: query last pay period and suggest next period if today is past the last period end
    React.useEffect(() => {
        if (!token) return;
        const controller = new AbortController();
        (async () => {
            try {
                const data = await gqlRequest({
                    token,
                    query: LAST_PAY_PERIOD_QUERY,
                    variables: {},
                    signal: controller.signal,
                });
                const periodEnd = data?.lastPayPeriod?.periodEnd;
                if (!periodEnd) return;

                // Next period starts the day after the last period end
                const [y, m, d] = periodEnd.split('-').map(Number);
                const nextStart = new Date(Date.UTC(y, m - 1, d + 1));
                const todayUtc  = new Date(Date.UTC(
                    new Date().getFullYear(), new Date().getMonth(), new Date().getDate()
                ));

                // Only suggest when today is on or after the next period start
                if (todayUtc >= nextStart) {
                    const nextEnd = new Date(Date.UTC(
                        nextStart.getUTCFullYear(),
                        nextStart.getUTCMonth() + 1,
                        0,   // last day of nextStart's month
                    ));
                    const fmtUtc = (dt) =>
                        `${dt.getUTCFullYear()}-` +
                        `${String(dt.getUTCMonth() + 1).padStart(2, '0')}-` +
                        `${String(dt.getUTCDate()).padStart(2, '0')}`;
                    setNextPeriodSuggestion({
                        suggestedStart: fmtUtc(nextStart),
                        suggestedEnd:   fmtUtc(nextEnd),
                        lastPeriodEnd:  periodEnd,
                    });
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    // Non-critical — ignore silently
                }
            }
        })();
        return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const filtered = useMemo(() => {
        let data = filter === 'All' ? employeesData : employeesData.filter(e => e.status === filter.toLowerCase());
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            data = data.filter(e => e.name?.toLowerCase().includes(q) || e.id?.toLowerCase().includes(q));
        }
        return data;
    }, [employeesData, filter, searchQuery]);

    // Recalculate summary cards live based on current CASH / tax choices.
    // CASH + No-Tax employees have deductions and super zeroed; net pay = gross.
    const adjustedSummary = useMemo(() => {
        return employeesData.reduce((acc, emp) => {
            const isCash  = paymentModes[emp.id] === 'CASH';
            const noTax   = isCash && taxChoices[emp.id] === false;
            acc.totalGrossPayable              += Number(emp.earnings ?? 0);
            acc.totalPaygDeductions            += noTax ? 0 : Number(emp.deductions ?? 0);
            acc.totalNetPayable                += noTax ? Number(emp.earnings ?? 0) : Number(emp.netPay ?? 0);
            acc.totalSuperannuationContribution += noTax ? 0 : Number(emp.superAmt ?? 0);
            acc.employeeCount                  += 1;
            return acc;
        }, { totalGrossPayable: 0, totalPaygDeductions: 0, totalNetPayable: 0, totalSuperannuationContribution: 0, employeeCount: 0 });
    }, [employeesData, paymentModes, taxChoices]);

    const abaStatusNorm = (periodAbaStatus?.abaStatus || '').toString().toUpperCase();
    const isAbaVoided = abaStatusNorm === 'VOIDED' || abaStatusNorm === 'VOID';

    // Derived: block Run Payroll button when ABA for this period must not be regenerated
    // Backend may block additional intermediate statuses too (e.g. PENDING_APPROVAL)
    const isAbaBlocked = (
        abaStatusNorm === 'PENDING_APPROVAL' ||
        abaStatusNorm === 'APPROVED' ||
        abaStatusNorm === 'RELEASED'
    );

    const isAlreadyPaid = (emp) => emp.payslipStatus === 'locked' || emp.payslipStatus === 'sent';
    const isZeroHours = (emp) =>
        (Number(emp.hours || 0) + Number(emp.otHours || 0) +
         Number(emp.saturdayHours || 0) + Number(emp.sundayHours || 0)) === 0;
    const selectable = filtered.filter(e => !isAlreadyPaid(e) && !isZeroHours(e));

    const allFilteredSelected = selectable.length > 0 && selectable.every(e => selectedIds.has(e.id));
    const someFilteredSelected = selectable.some(e => selectedIds.has(e.id));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                selectable.forEach(e => next.delete(e.id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                selectable.forEach(e => next.add(e.id));
                return next;
            });
        }
    };

    const toggleSelectOne = (id) => {
        const emp = filtered.find(e => e.id === id);
        if (!emp || isAlreadyPaid(emp) || isZeroHours(emp)) return;
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const selectedEmployees = selectable.filter(e => selectedIds.has(e.id));

    // BANK-payment employees in the current selection who are missing BSB or account number.
    // These will be silently excluded from the ABA file by the backend — warn the user upfront.
    const missingBankEmployees = selectedEmployees.filter(e => {
        const isCash = (paymentModes[e.id] || e.paymentMethod || 'BANK') === 'CASH';
        return !isCash && (!e.bsb || !e.accountNumber);
    });

    // BANK-payment employees whose bank details are present but invalid (basic format check).
    // Backend performs stricter validation; this is just an early warning to avoid "all recipients excluded".
    const invalidBankEmployees = selectedEmployees.filter(e => {
        const isCash = (paymentModes[e.id] || e.paymentMethod || 'BANK') === 'CASH';
        if (isCash) return false;
        if (!e.bsb || !e.accountNumber) return false;
        const bsbDigits = String(e.bsb).replace(/[^0-9]/g, '');
        const acctDigits = String(e.accountNumber).replace(/[^0-9]/g, '');
        const bsbOk = bsbDigits.length === 6;
        // Backend rule: 1–9 digits numeric only (see AbaValidatorService.validateAccountNumber)
        const acctOk = acctDigits.length >= 1 && acctDigits.length <= 9;
        return !bsbOk || !acctOk;
    });

    // ── Payment Mode helpers ────────────────────────────────────────────────

    // Fire-and-forget persist; calls onError() to rollback optimistic state if the DB write fails.
    const persistPaymentMode = async (empId, paymentMethod, isTaxCalculated, onError) => {
        try {
            await gqlRequest({
                token,
                query: SAVE_PAYMENT_MODE_MUTATION,
                variables: {
                    input: { employeeId: empId, startDate, endDate, paymentMethod, isTaxCalculated },
                },
            });
        } catch (err) {
            console.error('Failed to save payment mode to DB', err);
            onError?.();
        }
    };

    const handlePaymentModeChange = (empId, mode, empName) => {
        const prevMode = paymentModes[empId];
        const prevTax  = taxChoices[empId];
        setPaymentModes(prev => ({ ...prev, [empId]: mode }));
        if (mode === 'CASH') {
            setCashTaxPopup({ empId, empName });
        } else {
            // Switched back to BANK — clear any previous tax choice
            setTaxChoices(prev => { const n = { ...prev }; delete n[empId]; return n; });
            persistPaymentMode(empId, 'BANK', true, () => {
                setPaymentModes(prev => ({ ...prev, [empId]: prevMode }));
                if (prevTax !== undefined) setTaxChoices(prev => ({ ...prev, [empId]: prevTax }));
            });
        }
    };

    const handleCashTaxYes = () => {
        const { empId } = cashTaxPopup;
        const prevTax = taxChoices[empId];
        setTaxChoices(prev => ({ ...prev, [empId]: true }));
        setCashTaxPopup(null);
        persistPaymentMode(empId, 'CASH', true, () => {
            setTaxChoices(prev => ({ ...prev, [empId]: prevTax }));
        });
    };

    const handleCashTaxNo = () => {
        const { empId } = cashTaxPopup;
        const prevTax = taxChoices[empId];
        setTaxChoices(prev => ({ ...prev, [empId]: false }));
        setCashTaxPopup(null);
        persistPaymentMode(empId, 'CASH', false, () => {
            setTaxChoices(prev => ({ ...prev, [empId]: prevTax }));
        });
    };

    const handleViewPayslip = async (emp) => {
        // Always regenerate then refetch — ensures modal shows the latest
        // backend-calculated values (tax, super, net pay) not stale local state.
        try {
            setGeneratingForId(emp.id);
            await gqlRequest({
                token,
                query: GENERATE_PAYSLIP_MUTATION,
                variables: { empId: emp.id, start: startDate, end: endDate },
            });
            // Refetch full payroll data so modal gets fresh calculations from backend
            const freshEmployees = await loadPayrollData();
            const freshEmp = freshEmployees.find((e) => e.id === emp.id);
            setModal(freshEmp ?? emp);
        } catch (err) {
            console.error('Error generating payslip from View action', err);
            setModal(emp);
        } finally {
            setGeneratingForId(null);
        }
    };

    React.useEffect(() => {
        const loadDetails = async () => {
            if (!modal?.id || !token || !startDate || !endDate) return;
            setDetailLoading(true);
            setDetailError(null);
            try {
                const data = await gqlRequest({
                    token,
                    query: TIME_TRACKING_QUERY,
                    variables: { filter: { startDate, endDate, employeeId: modal.id } },
                });
                setDetailEntries(data?.payrollTimeTracking?.entries ?? []);
            } catch (err) {
                console.error('Error loading time tracking details:', err);
                setDetailEntries([]);
                setDetailError(err.message || 'Failed to load time tracking details');
            } finally {
                setDetailLoading(false);
            }
        };
        loadDetails();
    }, [modal?.id, startDate, endDate, token]);

    const triggerDownload = async (resolvedUrl, fileName) => {
        const fileRes = await fetch(resolvedUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!fileRes.ok) throw new Error(`Could not fetch PDF: HTTP ${fileRes.status}`);
        const blob = await fileRes.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
    };

    const downloadPayslip = async (emp) => {
        let payslipId = emp.payslipId;
        if (!payslipId) {
            const data = await gqlRequest({
                token,
                query: GENERATE_PAYSLIP_ID_ONLY_MUTATION,
                variables: { empId: emp.id, start: startDate, end: endDate },
            });
            payslipId = data?.generatePayslip?.id;
            if (!payslipId) throw new Error('Could not generate payslip');
        }
        const downloadUrl = `${API_BASE}/payroll/payslip-download/${payslipId}`;
        const safeLabel = `${emp.name}_${startDate}_${endDate}`.replace(/[^a-zA-Z0-9_\- ]/g, '_');
        await triggerDownload(downloadUrl, `${safeLabel}.pdf`);
    };

    const handleRunPayroll = async () => {
        setRunning(true);
        setRunError(null);
        try {
            if (selectedEmployees.length === 0) {
                setRunError('Please select at least one employee to run payroll.');
                setRunning(false);
                return;
            }

            // Build the minimal employee list — only identifiers and payment preferences.
            // All financial values (gross, tax, super, net) are recalculated by the
            // backend from timecards + employee DB data. The frontend values are
            // used for display only (runPayroll query) and are never sent to the API.
            const employees = selectedEmployees.map(emp => {
                const paymentMethod   = paymentModes[emp.id] || 'BANK';
                const isCash          = paymentMethod === 'CASH';
                const isTaxCalculated = isCash ? (taxChoices[emp.id] === true) : true;

                return {
                    employeeId:    emp.id,
                    paymentMethod,
                    isTaxCalculated,
                };
            });

            // Generate a one-time idempotency key for this button click.
            // The backend uses it to deduplicate network retries / double-clicks.
            const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

            // Single mutation: backend recalculates, saves all payslips, + auto-generates ABA file.
            const data = await gqlRequest({
                token,
                query: EXECUTE_PAYROLL_RUN_MUTATION,
                variables: { input: { startDate, endDate, employees, idempotencyKey } },
            });

            const result = data?.executePayrollRun;
            if (result?.failedCount > 0 && result?.successCount === 0) {
                const firstErr = result.employees?.find(e => e.error)?.error;
                throw new Error(firstErr || 'Payroll run failed for all employees.');
            }

            // Show ABA warning if file wasn't generated
            if (result?.abaGenerated === false && result?.abaError) {
                const excluded = Array.isArray(result?.abaExcludedRecipients) ? result.abaExcludedRecipients : null;
                if (excluded?.length) {
                    const lines = excluded.map(e =>
                        `${e.recipientName}${e.bsb ? ` (BSB: ${e.bsb})` : ''}${e.accountNumber ? ` (Acct: ${e.accountNumber})` : ''} — ${e.reason}`,
                    );
                    setAbaWarning(`${result.abaError}\n\nExcluded recipients:\n- ${lines.join('\n- ')}`);
                } else {
                    setAbaWarning(result.abaError);
                }
            } else {
                setAbaWarning(null);
            }

            // Show STP result banner
            setStpResult({
                submitted:    result?.stpSubmitted ?? false,
                submissionId: result?.stpSubmissionId ?? null,
                error:        result?.stpError ?? null,
            });

            // Reload so table reflects updated payslip status
            await loadPayrollData();
            setSelectedIds(new Set());
            setRunning(false);
            if (onPayrollComplete) onPayrollComplete();
        } catch (err) {
            console.error('Error running payroll', err);
            setRunError(err.message || 'Payroll run failed. Please try again.');
            setRunning(false);
        }
    };

    const handleVoidAbaForPeriod = async () => {
        const abaId = periodAbaStatus?.abaPayRunId;
        if (!abaId) return;
        if (!voidAbaReason.trim()) return;

        setVoidAbaBusy(true);
        setVoidAbaError(null);
        try {
            await gqlRequest({
                token,
                query: VOID_ABA_MUTATION,
                variables: { id: abaId, reason: voidAbaReason.trim() },
            });

            setVoidAbaOpen(false);
            setVoidAbaReason('');
            setRunError(null);
            // Refresh the period state so Run Payroll becomes enabled
            await loadPayrollData();
        } catch (e) {
            setVoidAbaError(e.message || 'Failed to void ABA pay run');
        } finally {
            setVoidAbaBusy(false);
        }
    };

    return (
        <div className="rp-page">

            {/* Header */}
            <div className="rp-header">
                <div>
                    <h2 className="rp-title">Run Payroll</h2>
                    <p className="rp-subtitle">Calculate and process employee pay for the selected period</p>
                </div>
                <div className="rp-header-actions">
                    <select
                        className="rp-range-select"
                        value={dateRange}
                        onChange={e => setDateRange(e.target.value)}
                    >
                        <option value="thisMonth">This Month</option>
                        <option value="lastMonth">Last Month</option>
                        <option value="last7">Last 7 Days</option>
                        <option value="last14">Last 14 Days</option>
                        <option value="last30">Last 30 Days</option>
                        <option value="custom">Custom Range</option>
                    </select>
                    {dateRange === 'custom' && (
                        <div className="rp-custom-range">
                            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                            <span>→</span>
                            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                        </div>
                    )}
                    <button
                        className="rp-run-btn"
                        onClick={() => {
                            if (selectedEmployees.length === 0) {
                                setNoEmployeePopup(true);
                            } else {
                                setConfirmOpen(true);
                            }
                        }}
                        disabled={running || loading || isAbaBlocked}
                        title={isAbaBlocked ? `ABA file for this period is ${periodAbaStatus?.abaStatus} — go to ABA Pay Runs to approve & download, or void to re-run with corrections` : ''}
                    >
                        <FiPlay size={14} />
                        {running ? 'Processing…' : selectedEmployees.length > 0 ? `Run Payroll (${selectedEmployees.length})` : 'Run Payroll'}
                    </button>
                </div>
            </div>

            {/* Run Payroll error banner */}
            {runError && (
                <div style={{
                    color: '#dc2626',
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    margin: '0 0 12px 0',
                    fontSize: '13px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '10px',
                }}>
                    <span style={{ flex: 1 }}>{runError}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {/ABA pay run already exists/i.test(runError) && onGoToAbaRuns && (
                            <>
                                <button
                                    onClick={() => { setRunError(null); onGoToAbaRuns(); }}
                                    style={{
                                        background: '#15803d', color: '#fff', border: 'none',
                                        borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                                        fontWeight: 600, padding: '4px 10px', whiteSpace: 'nowrap',
                                    }}
                                    title="Approve then Release to get the ABA file for your bank"
                                >
                                    Approve &amp; Download ABA →
                                </button>
                                <button
                                    onClick={() => {
                                        setRunError(null);
                                        setVoidAbaError(null);
                                        setVoidAbaReason('');
                                        if (periodAbaStatus?.abaPayRunId) {
                                            setVoidAbaOpen(true);
                                        } else {
                                            onGoToAbaRuns?.();
                                        }
                                    }}
                                    style={{
                                        background: '#dc2626', color: '#fff', border: 'none',
                                        borderRadius: '6px', cursor: 'pointer', fontSize: '12px',
                                        fontWeight: 600, padding: '4px 10px', whiteSpace: 'nowrap',
                                    }}
                                    title="Void the ABA run first if you need to re-run payroll with corrections"
                                >
                                    Void &amp; Re-run →
                                </button>
                            </>
                        )}
                        <button
                            onClick={() => setRunError(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '16px', lineHeight: 1 }}
                        >×</button>
                    </div>
                </div>
            )}

            {/* ABA warning banner — payslips saved but ABA file not generated */}
            {abaWarning && (
                <div style={{
                    color: '#92400e',
                    background: '#fffbeb',
                    border: '1px solid #fcd34d',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    margin: '0 0 12px 0',
                    fontSize: '13px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                }}>
                    <span>
                        <strong>Payslips saved, but ABA file was not generated.</strong><br />
                        {abaWarning.includes('not configured')
                            ? 'Go to Payroll Settings → ABA Company Banking and save your bank details first.'
                            : abaWarning}
                    </span>
                    <button
                        onClick={() => setAbaWarning(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontSize: '16px', lineHeight: 1, flexShrink: 0 }}
                    >×</button>
                </div>
            )}

            {/* STP submission result banner */}
            {stpResult && (
                <div style={{
                    color:        stpResult.submitted ? '#065f46' : '#92400e',
                    background:   stpResult.submitted ? '#ecfdf5' : '#fffbeb',
                    border:       `1px solid ${stpResult.submitted ? '#6ee7b7' : '#fcd34d'}`,
                    borderRadius: '10px',
                    padding:      '10px 14px',
                    margin:       '0 0 12px 0',
                    fontSize:     '13px',
                    display:      'flex',
                    justifyContent: 'space-between',
                    alignItems:   'flex-start',
                    gap:          '12px',
                }}>
                    <span>
                        {stpResult.submitted ? (
                            <>
                                <strong>✅ STP PAYEVENT submitted to ATO.</strong><br />
                                Submission ID: <code style={{ fontSize: '11px' }}>{stpResult.submissionId}</code>
                                {' — Check the STP tab for ATO response details.'}
                            </>
                        ) : (
                            <>
                                <strong>⚠ Payslips saved, but STP was not submitted.</strong><br />
                                {stpResult.error?.includes('credentials') || stpResult.error?.includes('SOFTWARE_ID')
                                    ? 'ATO credentials not configured. Add STP_SOFTWARE_ID and STP_MACHINE_CREDENTIAL to the server .env file.'
                                    : stpResult.error || 'STP submission failed. You can retry from the STP tab.'}
                            </>
                        )}
                    </span>
                    <button
                        onClick={() => setStpResult(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', lineHeight: 1, flexShrink: 0 }}
                    >×</button>
                </div>
            )}

            {/* Hard block banner — ABA APPROVED or RELEASED for this period */}
            {isAbaBlocked && (
                <div style={{
                    color: '#991b1b',
                    background: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    margin: '0 0 14px 0',
                    fontSize: '13px',
                    fontWeight: 500,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <strong>Run Payroll is disabled for this period.</strong>{' '}
                            An ABA file already has status <strong>{periodAbaStatus.abaStatus}</strong>{' '}
                            (ID: {periodAbaStatus.abaPayRunId?.slice(0, 8)}…).
                            {' '}Void the existing ABA pay run before reprocessing this period.
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            <button
                                onClick={() => {
                                    setVoidAbaError(null);
                                    setVoidAbaReason('');
                                    if (periodAbaStatus?.abaPayRunId) setVoidAbaOpen(true);
                                }}
                                disabled={!periodAbaStatus?.abaPayRunId || voidAbaBusy}
                                style={{
                                    background: '#dc2626',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: '6px 10px',
                                    whiteSpace: 'nowrap',
                                }}
                                title="Void this period's ABA pay run to re-run payroll"
                            >
                                Void ABA Run
                            </button>
                            {onGoToAbaRuns && (
                                <button
                                    onClick={() => onGoToAbaRuns()}
                                    style={{
                                        background: '#fff',
                                        color: '#991b1b',
                                        border: '1px solid #fca5a5',
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: '6px 10px',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title="Open ABA Pay Runs list"
                                >
                                    ABA Pay Runs →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Next period suggestion banner */}
            {nextPeriodSuggestion && (
                <div style={{
                    color: '#1e40af',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    margin: '0 0 12px 0',
                    fontSize: '13px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                }}>
                    <span>
                        <strong>New pay period available.</strong>{' '}
                        Last period ended <strong>{nextPeriodSuggestion.lastPeriodEnd}</strong>.{' '}
                        Next period: <strong>{nextPeriodSuggestion.suggestedStart}</strong> → <strong>{nextPeriodSuggestion.suggestedEnd}</strong>.
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                            onClick={() => {
                                setDateRange('custom');
                                setCustomStart(nextPeriodSuggestion.suggestedStart);
                                setCustomEnd(nextPeriodSuggestion.suggestedEnd);
                                setNextPeriodSuggestion(null);
                            }}
                            style={{
                                background: '#2563eb', color: '#fff', border: 'none',
                                borderRadius: 6, padding: '5px 12px', fontSize: 12,
                                fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            Switch to next period
                        </button>
                        <button
                            onClick={() => setNextPeriodSuggestion(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', fontSize: 16, lineHeight: 1 }}
                        >×</button>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className="rp-cards">
                <StatCard icon={<FiUsers />} iconColor="blue" label="Total Employees" value={adjustedSummary.employeeCount} />
                <StatCard icon={<FiDollarSign />} iconColor="purple" label="Total Gross Pay" value={fmt(adjustedSummary.totalGrossPayable)} />
                <StatCard icon={<FiPercent />} iconColor="blue" label="Total Tax (PAYG)" value={fmt(adjustedSummary.totalPaygDeductions)} />
                <StatCard icon={<FiShield />} iconColor="orange" label="Total Superannuation" value={fmt(adjustedSummary.totalSuperannuationContribution)} />
                <StatCard icon={<FiCreditCard />} iconColor="green" label="Total Net Pay" value={fmt(adjustedSummary.totalNetPayable)} />
            </div>

            {/* Employee Payroll Table */}
            <div className="rp-section">
                <div className="rp-table-toolbar">
                    {/* Search */}
                    <div className="rp-search-wrap">
                        <FiSearch className="rp-search-icon" size={15} />
                        <input
                            className="rp-search-input"
                            placeholder="Search employee..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="rp-toolbar-right">
                        {/* Period type selector */}
                        <div className="rp-period-select-wrap">
                            <FiCalendar size={14} className="rp-period-icon" />
                            <select
                                className="rp-period-select"
                                value={periodType}
                                onChange={e => setPeriodType(e.target.value)}
                            >
                                <option value="Week">Weekly</option>
                                <option value="Fortnight">Fortnightly</option>
                                <option value="Month">Monthly</option>
                                <option value="LastMonth">Last month</option>
                            </select>
                        </div>

                        {/* Download All */}
                        <button
                            className="rp-download-all-btn"
                            onClick={async () => {
                                for (const emp of filtered) {
                                    try {
                                        setDownloadingId(emp.id);
                                        await downloadPayslip(emp);
                                    } catch (err) {
                                        console.error('Download failed for', emp.name, err);
                                    }
                                }
                                setDownloadingId(null);
                            }}
                            disabled={filtered.length === 0}
                        >
                            <FiDownload size={14} />
                            Download All
                        </button>
                    </div>
                </div>

                <div className="rp-table-wrap">
                    {loadError && (
                        <div style={{
                            color: '#dc2626',
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '10px',
                            padding: '10px 12px',
                            marginBottom: '10px',
                            fontSize: '13px',
                        }}
                        >
                            {loadError}
                        </div>
                    )}
                    <table className="rp-table">
                        <thead>
                            <tr>
                                <th style={{ width: 36, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        ref={el => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                                        onChange={toggleSelectAll}
                                        title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </th>
                                <th>Employee</th>
                                <th>Type</th>
                                <th>Total Hours</th>
                                <th>OT / Penalty</th>
                                <th>Paid Leave</th>
                                <th>Unpaid Leave</th>
                                <th>Earnings</th>
                                <th>Deductions</th>
                                <th>Net Pay</th>
                                <th>Payment Mode</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={12}>
                                        <CommonLoader message="Loading payroll data…" />
                                    </td>
                                </tr>
                            ) : filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
                                        No payroll data for the selected period.
                                    </td>
                                </tr>
                            ) : filtered.map(emp => {
                                const isCash = paymentModes[emp.id] === 'CASH';
                                const noTax  = isCash && taxChoices[emp.id] === false;
                                return (
                                <tr
                                    key={emp.id}
                                    onClick={() => handleViewPayslip(emp)}
                                    style={{
                                        cursor: isZeroHours(emp) ? 'default' : 'pointer',
                                        opacity: isZeroHours(emp) ? 0.55 : 1,
                                        background: selectedIds.has(emp.id)
                                            ? '#f0f9ff'
                                            : isZeroHours(emp)
                                                ? '#f8fafc'   // grey tint — no hours
                                                : noTax
                                                    ? '#fffbeb'   // amber tint — CASH no-tax
                                                    : isCash
                                                        ? '#fefce8' // light yellow — CASH with tax
                                                        : undefined,
                                    }}
                                >
                                    <td
                                        style={{ textAlign: 'center' }}
                                        onClick={e => { e.stopPropagation(); toggleSelectOne(emp.id); }}
                                    >
                                        {isAlreadyPaid(emp) ? (
                                            <span style={{
                                                fontSize: '10px', fontWeight: 600,
                                                padding: '2px 7px', borderRadius: '10px',
                                                background: emp.payslipStatus === 'sent' ? '#dcfce7' : '#ede9fe',
                                                color: emp.payslipStatus === 'sent' ? '#15803d' : '#6d28d9',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {emp.payslipStatus === 'sent' ? 'Paid' : 'ABA Queued'}
                                            </span>
                                        ) : isZeroHours(emp) ? (
                                            <span
                                                title="No hours recorded — payroll cannot be generated"
                                                style={{
                                                    fontSize: '10px', fontWeight: 600,
                                                    padding: '2px 7px', borderRadius: '10px',
                                                    background: '#f1f5f9', color: '#64748b',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                0h
                                            </span>
                                        ) : (
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(emp.id)}
                                                onChange={() => toggleSelectOne(emp.id)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        <div className="rp-emp-cell">
                                            <div>
                                                <div className="rp-emp-name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {emp.name}
                                                    {(paymentModes[emp.id] || emp.paymentMethod || 'BANK') !== 'CASH' && (!emp.bsb || !emp.accountNumber) && (
                                                        <span title="Missing bank details — this employee will be excluded from the ABA file" style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 3,
                                                            background: '#fef3c7', color: '#92400e',
                                                            border: '1px solid #fcd34d', borderRadius: 4,
                                                            fontSize: 10, fontWeight: 600, padding: '1px 5px',
                                                            whiteSpace: 'nowrap', cursor: 'default',
                                                        }}>
                                                            ⚠ No bank details
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="rp-emp-type">{emp.id}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{emp.type}</td>
                                    <td>{(Number(emp.hours || 0) + Number(emp.otHours || 0) + Number(emp.saturdayHours || 0) + Number(emp.sundayHours || 0)).toFixed(2)}h</td>
                                    <td style={{ color: (Number(emp.otHours || 0) + Number(emp.saturdayHours || 0) + Number(emp.sundayHours || 0)) > 0 ? '#d97706' : '#94a3b8' }}>
                                        {(Number(emp.otHours || 0) + Number(emp.saturdayHours || 0) + Number(emp.sundayHours || 0)).toFixed(2)}h
                                    </td>
                                    <td style={{ color: emp.paidLeave > 0 ? '#059669' : '#94a3b8' }}>{emp.paidLeave}h</td>
                                    <td style={{ color: emp.unpaidLeave > 0 ? '#dc2626' : '#94a3b8' }}>{emp.unpaidLeave}h</td>
                                    <td>{fmt(emp.earnings)}</td>
                                    <td style={{ color: noTax ? '#94a3b8' : '#dc2626' }}>
                                        {noTax
                                            ? <span title="Tax excluded — CASH no-tax employee">$0.00 <span style={{ fontSize: 10, color: '#d97706' }}>excl.</span></span>
                                            : fmt(emp.deductions)}
                                    </td>
                                    <td className="rp-net-pay">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <span>{noTax ? fmt(emp.earnings) : fmt(emp.netPay)}</span>
                                            {isCash && (
                                                <span style={{
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    color: '#92400e',
                                                    background: '#fef3c7',
                                                    borderRadius: 4,
                                                    padding: '1px 5px',
                                                    alignSelf: 'flex-start',
                                                }}>
                                                    {noTax ? 'CASH · No ABA · No Tax' : 'CASH · No ABA · Tax Incl.'}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td onClick={e => e.stopPropagation()} style={{ minWidth: 110 }}>
                                        {isAlreadyPaid(emp) ? (
                                            <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                                        ) : (
                                            <select
                                                value={paymentModes[emp.id] || 'BANK'}
                                                onChange={e => handlePaymentModeChange(emp.id, e.target.value, emp.name)}
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: 6,
                                                    border: '1px solid #e2e8f0',
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    background: paymentModes[emp.id] === 'CASH' ? '#fffbeb' : '#f0fdf4',
                                                    color: paymentModes[emp.id] === 'CASH' ? '#92400e' : '#15803d',
                                                }}
                                            >
                                                <option value="BANK">BANK</option>
                                                <option value="CASH">CASH</option>
                                            </select>
                                        )}
                                    </td>
                                    <td style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <button
                                            className="rp-generate-btn"
                                            onClick={(e) => { e.stopPropagation(); handleViewPayslip(emp); }}
                                            disabled={generatingForId === emp.id}
                                        >
                                            {generatingForId === emp.id ? 'Generating…' : <>View <FiChevronRight size={12} /></>}
                                        </button>
                                        {onViewTimesheet && (
                                            <button
                                                className="rp-generate-btn"
                                                style={{ background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}
                                                onClick={(e) => { e.stopPropagation(); onViewTimesheet(emp.name); }}
                                                title="View Timesheet"
                                            >
                                                <FiClock size={12} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );})}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CASH Payment — Tax & Deductions Choice Popup */}
            {cashTaxPopup && (
                <div className="rp-confirm-overlay">
                    <div className="rp-confirm-modal">
                        <div className="rp-confirm-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                            <FiDollarSign size={28} />
                        </div>
                        <h3 className="rp-confirm-title">Cash Payment Selected</h3>
                        <p className="rp-confirm-body">
                            <strong>{cashTaxPopup.empName}</strong> will be paid in <strong>CASH</strong> and excluded from the ABA file.
                        </p>
                        <p className="rp-confirm-body">
                            Do you want to calculate <strong>Tax and Deductions</strong> for this employee?
                        </p>
                        <div className="rp-confirm-footer">
                            <button className="rp-confirm-cancel" onClick={handleCashTaxNo}>
                                No — Zero Tax
                            </button>
                            <button className="rp-confirm-proceed" style={{ background: '#d97706' }} onClick={handleCashTaxYes}>
                                Yes — Calculate Tax
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Run Payroll Confirmation Modal */}
            {confirmOpen && (
                <div className="rp-confirm-overlay" onClick={() => setConfirmOpen(false)}>
                    <div className="rp-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="rp-confirm-icon"><FiPlay size={28} /></div>
                        <h3 className="rp-confirm-title">Run Payroll?</h3>
                        <p className="rp-confirm-body">
                            You are about to process payroll for{' '}
                            <strong>{selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''}</strong>{' '}
                            for the period <strong>{startDate}</strong> to <strong>{endDate}</strong>.
                        </p>

                        {/* ── ABA status notice for this period ── */}
                        {periodAbaStatus && !isAbaVoided && (
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #f59e0b',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                                fontSize: 12,
                                color: '#92400e',
                            }}>
                                <strong>⚠ ABA file already exists for this period.</strong><br />
                                Status: <strong>{periodAbaStatus.abaStatus}</strong> (ID: {periodAbaStatus.abaPayRunId?.slice(0, 8)}…)<br />
                                Running payroll again will generate a new ABA file and may result in duplicate payments.
                            </div>
                        )}

                        {periodAbaStatus && isAbaVoided && (
                            <div style={{
                                background: '#ecfeff',
                                border: '1px solid #67e8f9',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                                fontSize: 12,
                                color: '#155e75',
                            }}>
                                <strong>ℹ Previous ABA pay run was voided.</strong><br />
                                Status: <strong>{periodAbaStatus.abaStatus}</strong> (ID: {periodAbaStatus.abaPayRunId?.slice(0, 8)}…)<br />
                                You can run payroll again to generate a new ABA file for this period.
                            </div>
                        )}

                        {/* ── Warning: some employees already locked / paid ── */}
                        {alreadyLockedCount > 0 && (
                            <div style={{
                                background: '#ede9fe',
                                border: '1px solid #a78bfa',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                                fontSize: 12,
                                color: '#4c1d95',
                            }}>
                                <strong>ℹ {alreadyLockedCount} employee{alreadyLockedCount !== 1 ? 's' : ''} already paid / ABA queued</strong> for this period.<br />
                                They are excluded from the selection — only unpaid employees will be processed.
                            </div>
                        )}

                        {/* ── Warning: selected BANK employees with missing bank details ── */}
                        {missingBankEmployees.length > 0 && (
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #f59e0b',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                                fontSize: 12,
                                color: '#92400e',
                            }}>
                                <strong>⚠ {missingBankEmployees.length} employee{missingBankEmployees.length !== 1 ? 's' : ''} missing bank details</strong><br />
                                The following employees are set to <strong>BANK</strong> payment but have no BSB or account number saved.
                                They will be <strong>excluded from the ABA file</strong> and will not be paid via bank transfer:
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: 16 }}>
                                    {missingBankEmployees.map(e => (
                                        <li key={e.id} style={{ marginBottom: 2 }}>
                                            {e.name}
                                            {!e.bsb && !e.accountNumber ? ' — no BSB or account number' :
                                             !e.bsb ? ' — no BSB' : ' — no account number'}
                                        </li>
                                    ))}
                                </ul>
                                <div style={{ marginTop: 6 }}>
                                    Update their bank details in <strong>Workforce → Employee Profile</strong> before running payroll to include them in the ABA file.
                                </div>
                            </div>
                        )}

                        {/* ── Warning: selected BANK employees with INVALID bank details ── */}
                        {invalidBankEmployees.length > 0 && (
                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 10,
                                fontSize: 12,
                                color: '#991b1b',
                            }}>
                                <strong>⚠ {invalidBankEmployees.length} employee{invalidBankEmployees.length !== 1 ? 's' : ''} have invalid bank details</strong><br />
                                Their BSB/account format looks wrong (BSB should be 6 digits; account number typically 6–10 digits). They may be excluded from the ABA file:
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: 16 }}>
                                    {invalidBankEmployees.map(e => (
                                        <li key={e.id} style={{ marginBottom: 2 }}>
                                            {e.name} — BSB: <code style={{ fontSize: 11 }}>{String(e.bsb)}</code>, Acct: <code style={{ fontSize: 11 }}>{String(e.accountNumber)}</code>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <p className="rp-confirm-body" style={{ color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                            This will generate payslips and create an ABA payment file. This action cannot be undone.
                        </p>
                        <div className="rp-confirm-footer">
                            <button className="rp-confirm-cancel" onClick={() => setConfirmOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="rp-confirm-proceed"
                                onClick={() => { setConfirmOpen(false); handleRunPayroll(); }}
                            >
                                <FiPlay size={13} /> Yes, Run Payroll
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* No Employee Selected Warning Popup */}
            {noEmployeePopup && (
                <div className="rp-confirm-overlay" onClick={() => setNoEmployeePopup(false)}>
                    <div className="rp-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="rp-confirm-icon" style={{ background: '#fffbeb', color: '#d97706' }}>
                            ⚠️
                        </div>
                        <h3 className="rp-confirm-title">No Employee Selected</h3>
                        <p className="rp-confirm-body">
                            Please select at least one employee from the table before running payroll.
                        </p>
                        <div className="rp-confirm-footer">
                            <button
                                className="rp-confirm-proceed"
                                style={{ background: '#7c3aed' }}
                                onClick={() => setNoEmployeePopup(false)}
                            >
                                OK, Select Employees
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Void ABA Pay Run (for current period) */}
            {voidAbaOpen && (
                <div className="rp-confirm-overlay" onClick={() => !voidAbaBusy && setVoidAbaOpen(false)}>
                    <div className="rp-confirm-modal" onClick={e => e.stopPropagation()}>
                        <div className="rp-confirm-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                            ⚠️
                        </div>
                        <h3 className="rp-confirm-title">Void ABA Pay Run?</h3>
                        <p className="rp-confirm-body">
                            This will void the ABA pay run for <strong>{startDate}</strong> to <strong>{endDate}</strong>
                            {periodAbaStatus?.abaPayRunId ? (
                                <> (ID: <code style={{ fontSize: 11 }}>{periodAbaStatus.abaPayRunId.slice(0, 8)}…</code>)</>
                            ) : null}
                            {' '}and unlock this period so you can re-run payroll.
                        </p>

                        {voidAbaError && (
                            <div style={{
                                marginTop: 8,
                                color: '#991b1b',
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: 8,
                                padding: '8px 10px',
                                fontSize: 12,
                            }}>
                                {voidAbaError}
                            </div>
                        )}

                        <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Reason</div>
                            <textarea
                                value={voidAbaReason}
                                onChange={(e) => setVoidAbaReason(e.target.value)}
                                placeholder="e.g. Incorrect timesheet, regenerating payroll"
                                rows={3}
                                style={{
                                    width: '100%',
                                    borderRadius: 8,
                                    border: '1px solid #e5e7eb',
                                    padding: '8px 10px',
                                    fontSize: 13,
                                    resize: 'vertical',
                                }}
                                autoFocus
                                disabled={voidAbaBusy}
                            />
                        </div>

                        <div className="rp-confirm-footer">
                            <button
                                className="rp-confirm-cancel"
                                onClick={() => setVoidAbaOpen(false)}
                                disabled={voidAbaBusy}
                            >
                                Cancel
                            </button>
                            <button
                                className="rp-confirm-proceed"
                                style={{ background: '#dc2626' }}
                                onClick={handleVoidAbaForPeriod}
                                disabled={voidAbaBusy || !voidAbaReason.trim() || !periodAbaStatus?.abaPayRunId}
                                title={!periodAbaStatus?.abaPayRunId ? 'No ABA pay run found for this period' : ''}
                            >
                                {voidAbaBusy ? 'Voiding…' : 'Confirm Void'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payslip Detail Overlay */}
            {modal && (() => {
                // Derive CASH/noTax state for the modal employee so all displayed
                // values match exactly what the backend will store on the payslip.
                const modalIsCash = paymentModes[modal.id] === 'CASH';
                const modalNoTax  = modalIsCash && taxChoices[modal.id] === false;
                const modalDeductions  = modalNoTax ? 0 : Number(modal.deductions ?? 0);
                const modalNetPay      = modalNoTax ? Number(modal.earnings ?? 0) : Number(modal.netPay ?? 0);
                const modalSuperAmt    = modalNoTax ? 0 : Number(modal.superAmt ?? 0);
                const modalIncomeTax   = modalNoTax ? 0 : Number(modal.incomeTax ?? (modal.deductions - (modal.medicareLevy ?? 0) - (modal.stslRepayment ?? 0)));
                const modalMedicare    = modalNoTax ? 0 : Number(modal.medicareLevy ?? 0);
                const modalStsl        = modalNoTax ? 0 : Number(modal.stslRepayment ?? 0);
                return (
                <div className="rp-detail-overlay" onClick={() => setModal(null)}>
                    <div className="rp-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="rp-detail-top">
                            <button className="rp-plain-btn" onClick={() => setModal(null)}>
                                <FiChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to Payroll
                            </button>
                            <div className="rp-detail-actions">
                                <button
                                    className="rp-download-btn"
                                    onClick={async () => {
                                        setDownloadingId(modal.id);
                                        try {
                                            await downloadPayslip(modal);
                                        } catch (err) {
                                            console.error('Download failed:', err);
                                            toast.error(`Download failed: ${err.message}`);
                                        } finally {
                                            setDownloadingId(null);
                                        }
                                    }}
                                    disabled={downloadingId === modal.id || generatingForId === modal.id}
                                >
                                    {downloadingId === modal.id ? 'Downloading…' : 'Download PDF'}
                                </button>
                                <button className="rp-print-btn" onClick={() => window.print()}>Print</button>
                            </div>
                        </div>

                        <div className="rp-slip-card">
                            <div className="rp-slip-header">
                                <div className="rp-slip-left">
                                    <div className="rp-slip-title">{modal.name}</div>
                                    <div className="rp-slip-subline">
                                        <FiCalendar size={12} />
                                        <span>{fmtMonthYear(startDate, endDate)}</span>
                                        <span className="rp-pill rp-pill-blue">{modal.type || '—'}</span>
                                        <span className="rp-slip-rate">Base Rate {fmt(modal.rate)}/hr</span>
                                    </div>
                                </div>
                            </div>

                            <div className="rp-kpi-grid">
                                <div className="rp-kpi-card">
                                    <div className="rp-kpi-top"><span>Regular Hours</span><span className="rp-kpi-dot blue" /></div>
                                    <b>{Number(modal.hours || 0).toFixed(1)} hrs</b>
                                    <small>{fmt(calcRegularPay(modal))} ({fmt(modal.rate)}/hr)</small>
                                </div>
                                <div className="rp-kpi-card">
                                    <div className="rp-kpi-top"><span>Overtime Hours</span><span className="rp-kpi-dot orange" /></div>
                                    <b>{Number(modal.otHours || 0).toFixed(1)} hrs</b>
                                    <small>{fmt(calcOvertimePay(modal))} ({modal.overtimeMultiplier ? `${modal.overtimeMultiplier}×` : '1.5×'} rate)</small>
                                </div>
                                {Number(modal.otTier2Hours || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>OT Tier 1 (≤2h)</span><span className="rp-kpi-dot orange" /></div>
                                        <b>{Number(modal.otTier1Hours || 0).toFixed(1)} hrs</b>
                                        <small>{fmt(modal.otTier1Pay ?? 0)} ({modal.overtimeMultiplier ? `${modal.overtimeMultiplier}×` : '1.5×'} rate)</small>
                                    </div>
                                )}
                                {Number(modal.otTier2Hours || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>OT Tier 2 (&gt;2h)</span><span className="rp-kpi-dot red" /></div>
                                        <b>{Number(modal.otTier2Hours || 0).toFixed(1)} hrs</b>
                                        <small>{fmt(modal.otTier2Pay ?? 0)} (2× rate)</small>
                                    </div>
                                )}
                                {Number(modal.callBackHoursApplied || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>Call-Back Min.</span><span className="rp-kpi-dot blue" /></div>
                                        <b>+{Number(modal.callBackHoursApplied).toFixed(1)} hrs</b>
                                        <small>Min 3h guarantee applied</small>
                                    </div>
                                )}
                                {Number(modal.casualLoadingRate || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>Casual Loading</span><span className="rp-kpi-dot green" /></div>
                                        <b>{(Number(modal.casualLoadingRate) * 100).toFixed(0)}%</b>
                                        <small>Applied to base rate</small>
                                    </div>
                                )}
                                {Number(modal.saturdayHours || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>Saturday Hours</span><span className="rp-kpi-dot orange" /></div>
                                        <b>{Number(modal.saturdayHours).toFixed(1)} hrs</b>
                                        <small>{fmt(modal.saturdayPay ?? 0)} ({modal.saturdayMultiplier ? `${modal.saturdayMultiplier}×` : '1.5×'} rate)</small>
                                    </div>
                                )}
                                {Number(modal.sundayHours || 0) > 0 && (
                                    <div className="rp-kpi-card">
                                        <div className="rp-kpi-top"><span>Sunday Hours</span><span className="rp-kpi-dot purple" /></div>
                                        <b>{Number(modal.sundayHours).toFixed(1)} hrs</b>
                                        <small>{fmt(modal.sundayPay ?? 0)} ({modal.sundayMultiplier ? `${modal.sundayMultiplier}×` : '2×'} rate)</small>
                                    </div>
                                )}
                                <div className="rp-kpi-card">
                                    <div className="rp-kpi-top"><span>Gross Pay</span><span className="rp-kpi-cash">$</span></div>
                                    <b>{fmt(modal.earnings)}</b>
                                    <small>Before deductions</small>
                                </div>
                                <div className="rp-kpi-card">
                                    <div className="rp-kpi-top"><span>Superannuation</span><span className="rp-kpi-dot green" /></div>
                                    <b>{modalNoTax ? '$0.00' : fmt(modalSuperAmt)}</b>
                                    <small>
                                        {modalNoTax
                                            ? 'Excluded — CASH no-tax'
                                            : `${modal.superannuationRate ?? 12}% on OTE ${fmt(modal.ordinaryTimeEarnings ?? modal.earnings)}${Number(modal.paidLeavePay || 0) > 0 ? ` (incl. leave ${fmt(modal.paidLeavePay)})` : ''}`
                                        }
                                    </small>
                                </div>
                            </div>

                            {/* ── Leave Accrual Section ───────────────────────── */}
                            {(Number(modal.annualLeaveAccrued || 0) > 0 || Number(modal.sickLeaveAccrued || 0) > 0) && (
                                <div className="rp-detail-section">
                                    <div className="rp-detail-title">Leave Accrual This Period</div>
                                    <div className="rp-kpi-grid">
                                        <div className="rp-kpi-card">
                                            <div className="rp-kpi-top"><span>Annual Leave</span><span className="rp-kpi-dot blue" /></div>
                                            <b>{Number(modal.annualLeaveAccrued || 0).toFixed(2)} hrs</b>
                                            <small>
                                                {Number(modal.workedRatio || 0) < 1
                                                    ? `${(Number(modal.workedRatio) * 100).toFixed(0)}% of period worked`
                                                    : 'Full period'}
                                            </small>
                                        </div>
                                        <div className="rp-kpi-card">
                                            <div className="rp-kpi-top"><span>Personal / Sick Leave</span><span className="rp-kpi-dot green" /></div>
                                            <b>{Number(modal.sickLeaveAccrued || 0).toFixed(2)} hrs</b>
                                            <small>
                                                {Number(modal.workedRatio || 0) < 1
                                                    ? `${(Number(modal.workedRatio) * 100).toFixed(0)}% of period worked`
                                                    : 'Full period'}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="rp-accrual-note">
                                        Based on {Number(modal.hours || 0).toFixed(1)} hrs worked.
                                        Annual leave: 160h/yr · Personal leave: 80h/yr (AU Fair Work Act).
                                        {modal.type && modal.type.toLowerCase().includes('casual') && (
                                            <span> Casual employees do not accrue leave.</span>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="rp-detail-section">
                                <div className="rp-detail-title">Working Hours Breakdown</div>
                                {detailLoading && <div className="rp-detail-note">Loading hours…</div>}
                                {detailError && <div className="rp-detail-note error">{detailError}</div>}
                                {!detailLoading && !detailError && (
                                    <>
                                        <div className="rp-subsection-title">Normal Working Hours (Mon – Fri)</div>
                                        <table className="rp-detail-table">
                                            <thead>
                                                <tr>
                                                    <th className="rp-date-cell">Date</th><th>Day Type</th><th>Regular Hours</th>
                                                    <th>Rate</th><th>Regular Pay</th><th>Leave</th><th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    // Daily ordinary-hours threshold — must match backend DEFAULT_DAILY_REGULAR_HOURS (8h)
                                                    const DAILY_THRESHOLD = 8;

                                                    // Only weekday entries (Mon–Fri) with payable hours.
                                                    // Total day hours = regular (timeIn/Out) + explicit OT, capped at threshold for display.
                                                    const weekdayWorkedRows = detailEntries.filter(r => {
                                                        const totalDay = toRoundedHours(r.regularHours) + Number(r.overtimeHours || 0);
                                                        const capped = Math.min(totalDay, DAILY_THRESHOLD);
                                                        return capped > 0 && getDayType(r.workDate) === 'Weekday';
                                                    });
                                                    const workedDates = new Set(weekdayWorkedRows.map(r => r.workDate));

                                                    // All weekdays in the pay period with no clock-in → show as Leave
                                                    const allWeekdays = getWeekdaysInRange(startDate, endDate);
                                                    const leaveDates = allWeekdays.filter(d => !workedDates.has(d));

                                                    // Build weekday worked rows — cap regular hours at daily threshold (matches backend)
                                                    const worked = weekdayWorkedRows.map((r, idx) => {
                                                        const totalDay = toRoundedHours(r.regularHours) + Number(r.overtimeHours || 0);
                                                        const cappedHrs = Math.min(totalDay, DAILY_THRESHOLD);
                                                        const regPay = cappedHrs * Number(modal.rate || 0);
                                                        return (
                                                            <tr key={`reg-${idx}`}>
                                                                <td className="rp-date-cell">{fmtDateShort(r.workDate)}</td>
                                                                <td><DayBadge dateStr={r.workDate} /></td>
                                                                <td>{cappedHrs.toFixed(2)} hrs</td>
                                                                <td>1×</td>
                                                                <td>{fmt(regPay)}</td>
                                                                <td>{r.leaveType ? <span className="rp-leave-badge">{r.leaveType}</span> : '—'}</td>
                                                                <td>{fmt(regPay)}</td>
                                                            </tr>
                                                        );
                                                    });

                                                    // Build leave rows for missing weekdays
                                                    const leave = leaveDates.map((d, idx) => (
                                                        <tr key={`leave-${idx}`} style={{ color: '#94a3b8' }}>
                                                            <td className="rp-date-cell">{fmtDateShort(d)}</td>
                                                            <td><DayBadge dateStr={d} /></td>
                                                            <td>0.00 hrs</td>
                                                            <td>—</td>
                                                            <td>{fmt(0)}</td>
                                                            <td><span className="rp-leave-badge">Leave</span></td>
                                                            <td>{fmt(0)}</td>
                                                        </tr>
                                                    ));

                                                    const all = [...worked, ...leave].sort((a, b) =>
                                                        a.key.localeCompare(b.key)
                                                    );
                                                    return all.length > 0 ? all : (
                                                        <tr><td colSpan={7} className="rp-empty-row">No regular hours for this period.</td></tr>
                                                    );
                                                })()}
                                            </tbody>
                                        </table>

                                        {/* Weekend / Penalty Hours — Saturday & Sunday shown separately */}
                                        {detailEntries.some(r => toRoundedHours(r.regularHours) > 0 && getDayType(r.workDate) !== 'Weekday') && (
                                            <>
                                                <div className="rp-subsection-title">Weekend / Penalty Hours (Sat & Sun)</div>
                                                <table className="rp-detail-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="rp-date-cell">Date</th><th>Day Type</th><th>Hours</th>
                                                            <th>Multiplier</th><th>Pay</th><th>Leave</th><th>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detailEntries
                                                            .filter(r => toRoundedHours(r.regularHours) > 0 && getDayType(r.workDate) !== 'Weekday')
                                                            .map((r, idx) => {
                                                                const roundedHrs = toRoundedHours(r.regularHours);
                                                                const dayType = getDayType(r.workDate);
                                                                const multiplier = dayType === 'Saturday'
                                                                    ? Number(modal.saturdayMultiplier || 1.5)
                                                                    : Number(modal.sundayMultiplier || 2);
                                                                const pay = roundedHrs * Number(modal.rate || 0) * multiplier;
                                                                return (
                                                                    <tr key={`wknd-${idx}`}>
                                                                        <td className="rp-date-cell">{fmtDateShort(r.workDate)}</td>
                                                                        <td><DayBadge dateStr={r.workDate} /></td>
                                                                        <td>{roundedHrs.toFixed(2)} hrs</td>
                                                                        <td style={{ color: '#d97706' }}>{multiplier}×</td>
                                                                        <td>{fmt(pay)}</td>
                                                                        <td>{r.leaveType ? <span className="rp-leave-badge">{r.leaveType}</span> : '—'}</td>
                                                                        <td>{fmt(pay)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </tbody>
                                                </table>
                                            </>
                                        )}

                                        <div className="rp-subsection-title">Overtime Working Hours</div>
                                        <table className="rp-detail-table">
                                            <thead>
                                                <tr>
                                                    <th className="rp-date-cell">Date</th><th>Day Type</th><th>OT Tier</th><th>OT Hours</th>
                                                    <th>Rate</th><th>OT Pay</th><th>Leave</th><th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    // Must match backend DEFAULT_DAILY_REGULAR_HOURS (8h)
                                                    const DAILY_THRESHOLD = 8;
                                                    const OT_TIER1_THRESHOLD = 2;

                                                    // An entry has OT when the full day (regular + explicit OT) exceeds the daily threshold.
                                                    // This mirrors calculateHoursBreakdown: shiftMinutes = regular + otHours, then split at threshold.
                                                    const otRows = detailEntries.filter(r => {
                                                        const totalDay = toRoundedHours(r.regularHours) + Number(r.overtimeHours || 0);
                                                        return getDayType(r.workDate) === 'Weekday' && Math.max(0, totalDay - DAILY_THRESHOLD) > 0;
                                                    });
                                                    if (otRows.length === 0) {
                                                        return <tr><td colSpan={8} className="rp-empty-row">No overtime hours for this period.</td></tr>;
                                                    }
                                                    const t1Multiplier = Number(modal.overtimeMultiplier || 1.5);
                                                    const t2Multiplier = Number(modal.overtimeDoubleMultiplier || 2);
                                                    const baseRate = Number(modal.rate || 0);
                                                    const rows = [];
                                                    otRows.forEach((r, idx) => {
                                                        // Derive effective OT using the same daily-threshold logic as the backend
                                                        const totalDay = toRoundedHours(r.regularHours) + Number(r.overtimeHours || 0);
                                                        const effectiveOt = Math.max(0, totalDay - DAILY_THRESHOLD);
                                                        const tier1Hrs = Math.min(effectiveOt, OT_TIER1_THRESHOLD);
                                                        const tier2Hrs = Math.max(0, effectiveOt - OT_TIER1_THRESHOLD);
                                                        const leaveCell = r.leaveType ? <span className="rp-leave-badge">{r.leaveType}</span> : '—';
                                                        if (tier1Hrs > 0) {
                                                            const pay = tier1Hrs * baseRate * t1Multiplier;
                                                            rows.push(
                                                                <tr key={`ot-t1-${idx}`}>
                                                                    <td className="rp-date-cell">{fmtDateShort(r.workDate)}</td>
                                                                    <td><DayBadge dateStr={r.workDate} /></td>
                                                                    <td>Tier 1 (≤2h)</td>
                                                                    <td>{tier1Hrs.toFixed(2)} hrs</td>
                                                                    <td>{t1Multiplier}×</td>
                                                                    <td>{fmt(pay)}</td>
                                                                    <td>{leaveCell}</td>
                                                                    <td>{fmt(pay)}</td>
                                                                </tr>
                                                            );
                                                        }
                                                        if (tier2Hrs > 0) {
                                                            const pay = tier2Hrs * baseRate * t2Multiplier;
                                                            rows.push(
                                                                <tr key={`ot-t2-${idx}`}>
                                                                    <td className="rp-date-cell">{fmtDateShort(r.workDate)}</td>
                                                                    <td><DayBadge dateStr={r.workDate} /></td>
                                                                    <td>Tier 2 (&gt;2h)</td>
                                                                    <td>{tier2Hrs.toFixed(2)} hrs</td>
                                                                    <td>{t2Multiplier}×</td>
                                                                    <td>{fmt(pay)}</td>
                                                                    <td>{leaveCell}</td>
                                                                    <td>{fmt(pay)}</td>
                                                                </tr>
                                                            );
                                                        }
                                                    });
                                                    return rows;
                                                })()}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>

                            <div className="rp-detail-section">
                                <div className="rp-detail-title">Deductions & Net Pay</div>
                                <table className="rp-detail-table">
                                    <thead>
                                        <tr><th>Item</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {/* ── Earnings ── */}
                                        <tr>
                                            <td><strong>Gross Pay</strong></td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>Total earnings before deductions</td>
                                            <td style={{ textAlign: 'right' }}><strong>{fmt(modal.earnings)}</strong></td>
                                        </tr>

                                        {/* ── PAYG Deductions ── */}
                                        <tr style={{ background: '#fff7ed' }}>
                                            <td style={{ color: modalNoTax ? '#94a3b8' : '#c2410c' }}>Income Tax</td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>
                                                {modalNoTax ? 'Excluded — CASH no-tax employee' : `ATO PAYG withholding (Scale ${modal.type?.toLowerCase().includes('casual') ? '1/2' : '2'})`}
                                            </td>
                                            <td style={{ textAlign: 'right', color: modalNoTax ? '#94a3b8' : '#c2410c' }}>
                                                {modalNoTax ? '$0.00' : `− ${fmt(modalIncomeTax)}`}
                                            </td>
                                        </tr>
                                        <tr style={{ background: '#fff7ed' }}>
                                            <td style={{ color: modalNoTax ? '#94a3b8' : '#c2410c' }}>Medicare Levy</td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>
                                                {modalNoTax ? 'Excluded — CASH no-tax employee' : '2% levy (shade-in applies below $32,500/yr)'}
                                            </td>
                                            <td style={{ textAlign: 'right', color: modalNoTax ? '#94a3b8' : '#c2410c' }}>
                                                {modalNoTax ? '$0.00' : `− ${fmt(modalMedicare)}`}
                                            </td>
                                        </tr>
                                        {!modalNoTax && Number(modalStsl || 0) > 0 && (
                                            <tr style={{ background: '#fff7ed' }}>
                                                <td style={{ color: '#c2410c' }}>HECS / HELP</td>
                                                <td style={{ color: '#64748b', fontSize: 11 }}>Study &amp; Training Loan repayment (STSL)</td>
                                                <td style={{ textAlign: 'right', color: '#c2410c' }}>− {fmt(modalStsl)}</td>
                                            </tr>
                                        )}
                                        <tr style={{ background: '#fef2f2' }}>
                                            <td><strong>Total PAYG Withheld</strong></td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>
                                                {modalNoTax ? 'Excluded — CASH no-tax employee' : `Income Tax + Medicare${Number(modalStsl || 0) > 0 ? ' + HECS/HELP' : ''}`}
                                            </td>
                                            <td style={{ textAlign: 'right', color: modalNoTax ? '#94a3b8' : '#dc2626' }}>
                                                <strong>{modalNoTax ? '$0.00' : `− ${fmt(modalDeductions)}`}</strong>
                                            </td>
                                        </tr>

                                        {/* ── Net Pay ── */}
                                        <tr className="rp-total-row">
                                            <td><strong>Net Pay</strong></td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>
                                                {modalNoTax ? 'Gross Pay (no deductions — CASH no-tax)' : 'Gross Pay − Total PAYG Withheld'}
                                            </td>
                                            <td style={{ textAlign: 'right' }}><strong>{fmt(modalNetPay)}</strong></td>
                                        </tr>

                                        {/* ── Superannuation (employer contribution — not deducted from net pay) ── */}
                                        <tr style={{ background: '#f0fdf4' }}>
                                            <td style={{ color: modalNoTax ? '#94a3b8' : '#166534' }}><strong>Superannuation</strong></td>
                                            <td style={{ color: '#64748b', fontSize: 11 }}>
                                                {modalNoTax ? 'Excluded — CASH no-tax employee' : (
                                                    <>
                                                        Employer SGC {modal.superannuationRate ?? 12}% on OTE {fmt(modal.ordinaryTimeEarnings ?? modal.earnings)}
                                                        {Number(modal.paidLeavePay || 0) > 0 && (
                                                            <span style={{ display: 'block', marginTop: 2 }}>
                                                                OTE = Regular Pay + Paid Leave {fmt(modal.paidLeavePay)} — excl. OT &amp; penalty rates
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'right', color: modalNoTax ? '#94a3b8' : '#166534' }}>
                                                <strong>{modalNoTax ? '$0.00' : fmt(modalSuperAmt)}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="rp-detail-notes">
                                <div className="rp-detail-notes-title">Pay Rules Notes:</div>
                                <ul className="rp-detail-notes-list">
                                    <li>Overtime applies when worked &gt; 8 hrs/day or outside rostered hours</li>
                                    <li>Minimum call-back pay: 3 hours (applies when employee is recalled)</li>
                                    <li>First 2 hours overtime on Mon-Sat uses OT Normal rate, after 2 hours uses 2x rate</li>
                                    <li>Casual loading compensates for entitlements based on employment type</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}
        </div>
    );
}
